// English glosses for drill cards. The Portuguese prompt is conjugated in a
// specific tense, so the English translation must be conjugated to the matching
// tense too — not left in the base/present form.

export type EnglishTense = 'present' | 'preterite' | 'imperfect' | 'future'

// Irregular English simple-past forms for every verb used in a template.
const EN_PAST_IRREGULAR: Record<string, string> = {
  speak: 'spoke',
  buy: 'bought',
  sing: 'sang',
  eat: 'ate',
  drink: 'drank',
  sell: 'sold',
  run: 'ran',
  write: 'wrote',
  understand: 'understood',
  hear: 'heard',
  grow: 'grew',
  split: 'split',
  put: 'put',
  leave: 'left',
  sleep: 'slept',
  feel: 'felt',
  wear: 'wore',
  have: 'had',
  go: 'went',
  do: 'did',
  make: 'made',
  say: 'said',
  give: 'gave',
  bring: 'brought',
  see: 'saw',
  know: 'knew',
  get: 'got',
  come: 'came',
  drive: 'drove',
}

// English for the concrete Portuguese subject shown in the prompt.
export const SUBJECT_ENGLISH: Record<string, string> = {
  Eu: 'I',
  Tu: 'you',
  Você: 'you',
  Ele: 'he',
  Ela: 'she',
  Nós: 'we',
  'A gente': 'we',
  Vocês: 'you all',
  Eles: 'they',
  Elas: 'they',
}

function regularPast(word: string) {
  if (word === 'prefer') return 'preferred'
  if (word.endsWith('e')) return `${word}d`
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ied`
  return `${word}ed`
}

function bePresent(subjectEn: string) {
  if (subjectEn === 'I') return 'am'
  if (subjectEn === 'he' || subjectEn === 'she') return 'is'
  return 'are'
}

// Irregular English 3rd-person-singular present forms.
const EN_PRESENT_3SG_IRREGULAR: Record<string, string> = {
  have: 'has',
  go: 'goes',
  do: 'does',
}

// "he"/"she" needs the -s ending the base form lacks: speaks, watches, studies.
function present3sg(word: string): string {
  if (EN_PRESENT_3SG_IRREGULAR[word]) return EN_PRESENT_3SG_IRREGULAR[word]
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ies`
  if (/(s|sh|ch|x|z|o)$/.test(word)) return `${word}es`
  return `${word}s`
}

// Conjugate an English verb phrase (the first word is the verb, the rest a
// particle like "for" / "to") into the requested tense.
export function englishVerbForTense(phrase: string, tense: EnglishTense, subjectEn: string): string {
  const [head, ...rest] = phrase.split(' ')
  const tail = rest.length ? ` ${rest.join(' ')}` : ''

  if (head === 'can') {
    if (tense === 'preterite' || tense === 'imperfect') return `could${tail}`
    if (tense === 'future') return `will be able to${tail}`
    return `can${tail}`
  }

  if (head === 'be') {
    if (tense === 'preterite') return `${['I', 'he', 'she'].includes(subjectEn) ? 'was' : 'were'}${tail}`
    if (tense === 'imperfect') return `used to be${tail}`
    if (tense === 'future') return `will be${tail}`
    return `${bePresent(subjectEn)}${tail}`
  }

  if (tense === 'preterite') return `${EN_PAST_IRREGULAR[head] ?? regularPast(head)}${tail}`
  if (tense === 'imperfect') return `used to ${head}${tail}`
  if (tense === 'future') return `will ${head}${tail}`
  if (subjectEn === 'he' || subjectEn === 'she') return `${present3sg(head)}${tail}`
  return phrase
}

// Build the English gloss for a WH-question: "Where do you live?",
// "What did she buy?", "Who do you travel with?". The main verb stays in its
// base form — an auxiliary (do/does/did/will) carries tense and person — except
// "be", which inverts directly. `whTrailing` is a stranded preposition such as
// "with" for "Who … with?".
export function englishQuestion(
  wh: string,
  whTrailing: string | null,
  englishVerb: string,
  subject: string,
  tense: EnglishTense,
): string {
  const subjectEn = SUBJECT_ENGLISH[subject] ?? subject.toLocaleLowerCase('en')
  const [head, ...rest] = englishVerb.split(' ')
  const particle = rest.length ? ` ${rest.join(' ')}` : ''
  const trail = whTrailing ? ` ${whTrailing}` : ''

  if (head === 'be') {
    if (tense === 'future') return `${wh} will ${subjectEn} be${particle}${trail}?`
    const be =
      tense === 'present' ? bePresent(subjectEn) : ['I', 'he', 'she'].includes(subjectEn) ? 'was' : 'were'
    return `${wh} ${be} ${subjectEn}${particle}${trail}?`
  }

  const aux =
    tense === 'future'
      ? 'will'
      : tense === 'preterite' || tense === 'imperfect'
        ? 'did'
        : ['he', 'she', 'it'].includes(subjectEn)
          ? 'does'
          : 'do'
  // The imperfect keeps its habitual sense: "Where did you use to live?".
  const verbPart = tense === 'imperfect' ? `use to ${head}${particle}` : `${head}${particle}`
  return `${wh} ${aux} ${subjectEn} ${verbPart}${trail}?`
}

// Fill a template's translation pattern for a given subject and tense. Returns
// the core clause, lower-cased at the start (the caller capitalises and adds the
// trailing phrase + period).
export function resolveTranslation(pattern: string, englishVerb: string, subject: string, tense: EnglishTense, objectEn?: string): string {
  const subjectEn = SUBJECT_ENGLISH[subject] ?? subject.toLocaleLowerCase('en')
  let out = pattern.replace('{pronoun}', subjectEn).replace('{verb}', englishVerbForTense(englishVerb, tense, subjectEn))
  if (objectEn !== undefined) out = out.replace('{object}', objectEn)
  return out
}

// Portuguese subjects that are plural, so the English noun has to be too.
export const PLURAL_SUBJECTS = new Set(['Nós', 'Eles', 'Elas', 'Vocês'])

// A template's English pattern for this subject: most are the same for everyone,
// but "{pronoun} {verb} a student" has to become "… students" for "they".
// ("A gente" is grammatically singular in Portuguese but "we" in English, so it
// takes the plural English pattern even though it isn't in PLURAL_SUBJECTS.)
export function patternForSubject(template: { englishTranslationPattern: string; englishTranslationPatternPlural?: string }, subject: string): string {
  return (PLURAL_SUBJECTS.has(subject) || subject === 'A gente') && template.englishTranslationPatternPlural ? template.englishTranslationPatternPlural : template.englishTranslationPattern
}
